import dayjs from "dayjs";
import { FC } from "react";
import { Tooltip } from "react-tooltip";

/**
 * The backend stamps CreatedBy / UpdatedBy with -1 when no admin was in the request:
 * Hangfire jobs, payment webhooks, customer-facing flows, startup initializers.
 * No user account has that id, so the names come back empty — we label it "System".
 */
export const SYSTEM_USER_ID = -1;
const SYSTEM_USER_LABEL = "System";

interface AuditAuthorProps {
  createdById?: number;
  createdByName: string;
  createdByLastName: string;
  updatedById?: number;
  updatedByName: string;
  updatedByLastName: string;
  insertDate: string;
  updateDate: string;
}

type AuthorKind = "user" | "system" | "unknown";

interface ResolvedAuthor {
  label: string;
  initial: string;
  kind: AuthorKind;
}

/**
 * Three states come back from the API:
 *  - id -1 with empty names: a job, webhook or customer flow — "System";
 *  - a positive id with names: a live admin account;
 *  - a positive id with empty names: the admin account was removed since (accounts are
 *    hard-deleted), so only the id is left — "Unknown user #id".
 * A missing or zero id means the row predates audit stamping and there is nothing to show.
 * @param id - the CreatedBy / UpdatedBy id from the API
 * @param firstName - resolved first name, empty when no account matched
 * @param lastName - resolved last name, empty when no account matched
 * @returns what to render, or null when there is nothing to show
 */
const resolveAuthor = (
  id: number | undefined,
  firstName: string,
  lastName: string
): ResolvedAuthor | null => {
  if (id === SYSTEM_USER_ID) {
    return { label: SYSTEM_USER_LABEL, initial: SYSTEM_USER_LABEL.charAt(0), kind: "system" };
  }
  const label = typeof firstName === "string" ? `${firstName} ${lastName ?? ""}`.trim() : "";
  if (label) return { label, initial: firstName.charAt(0), kind: "user" };
  if (typeof id === "number" && id > 0) {
    return { label: `Unknown user #${id}`, initial: "?", kind: "unknown" };
  }
  return null;
};

const BADGE_COLOR: Record<AuthorKind, string> = {
  user: "bg-gray-700 text-white",
  system: "bg-gray-400 text-white",
  unknown: "bg-gray-300 text-gray-700"
};

/**
 * Display name for an audit stamp, for pages that print it as inline text instead of a badge.
 * @param id - the CreatedBy / UpdatedBy id; -1 means the platform itself
 * @param firstName - resolved first name, empty when no user matched
 * @param lastName - resolved last name, empty when no user matched
 * @returns "System", the trimmed full name, or null when there is nothing to show
 */
export const formatAuditAuthorName = (
  id: number | undefined,
  firstName: string,
  lastName: string
): string | null => resolveAuthor(id, firstName, lastName)?.label ?? null;

const AuthorBadge: FC<{ author: ResolvedAuthor | null; tooltipId: string }> = ({ author, tooltipId }) =>
  author ? (
    <>
      <div
        data-tooltip-content={author.label}
        data-tooltip-id={tooltipId}
        className={`${BADGE_COLOR[author.kind]} ring-1 ring-gray-300
        font-medium h-6 w-6 rounded-full flex justify-center items-center mx-2 my-1 cursor-pointer`}
      >
        {author.initial}
      </div>
      <Tooltip id={tooltipId} className="z-50" />
    </>
  ) : (
    <span className="mx-2"> - </span>
  );

const AuditAuthor: FC<AuditAuthorProps> = ({
  insertDate,
  createdById,
  createdByName,
  createdByLastName,
  updatedById,
  updatedByName,
  updateDate,
  updatedByLastName
}) => {
  const createdBy = resolveAuthor(createdById, createdByName, createdByLastName);
  const updatedBy = resolveAuthor(updatedById, updatedByName, updatedByLastName);
  const hasUpdate = updateDate !== null && updateDate !== undefined;

  return (
    <span className="flex flex-col ">
      <span className="flex items-center text-gray-600">
        Created by:
        <AuthorBadge author={createdBy} tooltipId="createdBy" />
        At {insertDate ? dayjs(insertDate).format("YYYY/MM/DD") : null}
      </span>
      {hasUpdate ? (
        <span className="flex items-center text-gray-600">
          Updated by:
          <AuthorBadge author={updatedBy} tooltipId="updatedBy" />
          At {updateDate ? dayjs(updateDate).format("YYYY/MM/DD") : null}
        </span>
      ) : null}
    </span>
  );
};

export default AuditAuthor;
